import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors, type SortState } from '@/lib/sort';
import { cn } from '@/lib/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  KeyRound,
  LogOut,
  MoreHorizontal,
  RotateCcw,
  Search,
  ShieldAlert,
  ShieldCheck,
  UserCog,
  UserPlus,
  Users,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { EmployeeIdFormatView, PersonDetail, RoleName } from '@bits/shared/zod';

export const peopleAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people-access',
  component: PeopleAccessPage,
});

const ALL_ROLES: readonly RoleName[] = ['Admin', 'Finance', 'HR', 'PM', 'Sales', 'Employee'];

function todayIst(): string {
  const off = (5 * 60 + 30) * 60 * 1000;
  return new Date(Date.now() + off).toISOString().slice(0, 10);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

type DialogKind = 'edit' | 'roles' | 'password' | 'reset-2fa' | 'exit' | 'reactivate';

interface OpenDialog {
  kind: DialogKind;
  person: PersonDetail;
}

function PeopleAccessPage(): JSX.Element {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const peopleQuery = useQuery({
    queryKey: ['people-access'],
    queryFn: () => api.listPeople(),
  });

  const [createOpen, setCreateOpen] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState<OpenDialog | null>(null);
  const [topError, setTopError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('active');
  const [roleFilter, setRoleFilter] = React.useState<RoleName | 'all'>('all');

  const invalidatePeople = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['people-access'] });
  }, [queryClient]);

  const people = peopleQuery.data ?? [];
  const meId = meQuery.data?.person.id ?? null;
  const activeCount = people.filter((p) => p.status === 'active').length;
  const exitedCount = people.length - activeCount;
  const pending2faCount = people.filter(
    (p) => p.status === 'active' && !p.twoFactorVerified,
  ).length;

  const filtered = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    return people.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (roleFilter !== 'all' && !p.roles.includes(roleFilter)) return false;
      if (!needle) return true;
      return (
        p.name.toLowerCase().includes(needle) ||
        p.email.toLowerCase().includes(needle) ||
        (p.employeeId ?? '').toLowerCase().includes(needle)
      );
    });
  }, [people, search, statusFilter, roleFilter]);
  const { rows: visible, ...sorter } = useSortedRows(
    filtered,
    PEOPLE_SORT_ACCESSORS,
    PEOPLE_INITIAL_SORT,
  );

  const hasFilters = search.trim() !== '' || roleFilter !== 'all';

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            People & Access
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Manage staff, roles, passwords, and 2FA.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <UserPlus />
          New person
        </Button>
      </div>

      {topError ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{topError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label="Active staff" value={activeCount} hint={`${people.length} total`} />
        <StatTile
          label="2FA pending"
          value={pending2faCount}
          hint={pending2faCount === 0 ? 'Everyone is enrolled' : 'Active accounts not yet enrolled'}
          tone={pending2faCount === 0 ? 'good' : 'warn'}
        />
        <StatTile label="Exited" value={exitedCount} hint="Access revoked" />
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
              />
              <Input
                className="pl-9"
                placeholder="Search name, email or employee ID…"
                aria-label="Search people"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </div>
            <div
              role="tablist"
              aria-label="Status"
              className="flex rounded-xl border border-[hsl(var(--border))] p-0.5"
            >
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === s.value}
                  onClick={() => setStatusFilter(s.value)}
                  className={
                    'rounded-lg px-3 py-1.5 text-sm transition-colors ' +
                    (statusFilter === s.value
                      ? 'bg-[hsl(var(--brand))] text-white'
                      : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]')
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
            <Select
              aria-label="Filter by role"
              className="h-11 w-auto min-w-[150px]"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.currentTarget.value as RoleName | 'all')}
            >
              <option value="all">All roles</option>
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </div>
          <CardDescription>
            Showing {visible.length} of {people.length}
            {statusFilter === 'all' ? '' : ` · ${statusFilter}`}
            {roleFilter === 'all' ? '' : ` · ${roleFilter}`}
            {isSelfVisible(visible, meId)
              ? ' · role changes and exit are disabled on your own row to avoid self-lockout'
              : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {peopleQuery.isPending ? (
            <TableSkeleton />
          ) : people.length === 0 ? (
            <EmptyState
              title="No people yet"
              body="Add the first staff member to start assigning roles and access."
              action={<Button onClick={() => setCreateOpen(true)}>New person</Button>}
            />
          ) : visible.length === 0 ? (
            <EmptyState
              title="Nobody matches"
              body={
                hasFilters
                  ? 'Try a different search or clear the filters.'
                  : `No ${statusFilter} people.`
              }
              action={
                hasFilters ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setRoleFilter('all');
                    }}
                  >
                    Clear filters
                  </Button>
                ) : null
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/60 text-left text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                    <SortableTh
                      sortKey="name"
                      controller={sorter}
                      className="py-3 pl-8 pr-3 font-medium"
                    >
                      Person
                    </SortableTh>
                    <SortableTh
                      sortKey="employeeId"
                      controller={sorter}
                      className="px-3 py-3 font-medium"
                    >
                      Employee ID
                    </SortableTh>
                    <SortableTh
                      sortKey="roles"
                      controller={sorter}
                      className="px-3 py-3 font-medium"
                    >
                      Roles
                    </SortableTh>
                    <SortableTh
                      sortKey="security"
                      controller={sorter}
                      className="px-3 py-3 font-medium"
                    >
                      Security
                    </SortableTh>
                    <SortableTh
                      sortKey="status"
                      controller={sorter}
                      className="px-3 py-3 font-medium"
                    >
                      Status
                    </SortableTh>
                    <SortableTh
                      sortKey="joined"
                      controller={sorter}
                      className="px-3 py-3 font-medium"
                    >
                      Joined
                    </SortableTh>
                    <th className="py-3 pl-3 pr-8 text-right font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => {
                    const isSelf = p.id === meId;
                    const exited = p.status === 'exited';
                    return (
                      <tr
                        key={p.id}
                        className={
                          'group border-b border-[hsl(var(--border))] transition-colors last:border-b-0 hover:bg-[hsl(var(--accent))]/40 ' +
                          (exited ? 'text-[hsl(var(--muted-foreground))]' : '')
                        }
                      >
                        <td className="py-3 pl-8 pr-3">
                          <button
                            type="button"
                            className="flex items-center gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2"
                            onClick={() => setOpenDialog({ kind: 'edit', person: p })}
                          >
                            <Avatar name={p.name} muted={exited} />
                            <span className="flex min-w-0 flex-col">
                              <span className="flex items-center gap-2">
                                <span
                                  className={
                                    'truncate font-medium ' +
                                    (exited ? '' : 'text-[hsl(var(--foreground))]')
                                  }
                                >
                                  {p.name}
                                </span>
                                {isSelf ? <Badge variant="outline">You</Badge> : null}
                                {p.isCsuite ? (
                                  <Badge variant="default">
                                    {p.csuiteRole ? p.csuiteRole.toUpperCase() : 'C-suite'}
                                  </Badge>
                                ) : null}
                              </span>
                              <span className="truncate text-xs text-[hsl(var(--muted-foreground))]">
                                {p.email}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td className="px-3 py-3">
                          {p.employeeId ? (
                            <code className="rounded-md bg-[hsl(var(--muted))] px-1.5 py-0.5 font-mono text-xs text-[hsl(var(--foreground))]">
                              {p.employeeId}
                            </code>
                          ) : (
                            <span className="text-[hsl(var(--muted-foreground))]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1">
                            {[...p.roles].sort(byRoleRank).map((r) => (
                              <Badge
                                key={r}
                                variant={r === 'Admin' ? 'secondary' : 'muted'}
                                className={r === 'Admin' ? 'font-semibold' : undefined}
                              >
                                {r}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <SecurityCell person={p} />
                        </td>
                        <td className="px-3 py-3">
                          <StatusCell person={p} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-[hsl(var(--muted-foreground))]">
                          {formatDate(p.dateJoined)}
                        </td>
                        <td className="py-3 pl-3 pr-8">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="hidden md:inline-flex"
                              onClick={() => setOpenDialog({ kind: 'edit', person: p })}
                            >
                              Details
                            </Button>
                            <RowActionsMenu
                              person={p}
                              isSelf={isSelf}
                              onAction={(kind) => setOpenDialog({ kind, person: p })}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <EmployeeIdFormatCard onError={setTopError} />

      <CreatePersonDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={invalidatePeople}
      />

      {openDialog?.kind === 'edit' ? (
        <PersonDetailsDialog
          person={openDialog.person}
          onClose={() => setOpenDialog(null)}
          onError={setTopError}
          onSuccess={async () => {
            setOpenDialog(null);
            setTopError(null);
            await invalidatePeople();
          }}
        />
      ) : null}
      {openDialog?.kind === 'roles' ? (
        <SetRolesDialog
          person={openDialog.person}
          onClose={() => setOpenDialog(null)}
          onError={setTopError}
          onSuccess={async () => {
            setOpenDialog(null);
            setTopError(null);
            await invalidatePeople();
          }}
        />
      ) : null}
      {openDialog?.kind === 'password' ? (
        <ResetPasswordDialog
          person={openDialog.person}
          onClose={() => setOpenDialog(null)}
          onError={setTopError}
          onSuccess={async () => {
            setOpenDialog(null);
            setTopError(null);
            await invalidatePeople();
          }}
        />
      ) : null}
      {openDialog?.kind === 'reset-2fa' ? (
        <ConfirmDialog
          title="Reset 2FA?"
          description={`This deletes ${openDialog.person.name}'s 2FA credential and revokes all active sessions. They will need to re-enroll on next login.`}
          confirmLabel="Reset 2FA"
          confirmVariant="destructive"
          onClose={() => setOpenDialog(null)}
          onError={setTopError}
          onConfirm={() => api.resetPersonTwoFactor(openDialog.person.id)}
          onSuccess={async () => {
            setOpenDialog(null);
            setTopError(null);
            await invalidatePeople();
          }}
        />
      ) : null}
      {openDialog?.kind === 'exit' ? (
        <ExitPersonDialog
          person={openDialog.person}
          onClose={() => setOpenDialog(null)}
          onError={setTopError}
          onSuccess={async () => {
            setOpenDialog(null);
            setTopError(null);
            await invalidatePeople();
          }}
        />
      ) : null}
      {openDialog?.kind === 'reactivate' ? (
        <ConfirmDialog
          title={`Reactivate ${openDialog.person.name}?`}
          description="This restores their active status. They will still need their old password (or an admin reset)."
          confirmLabel="Reactivate"
          confirmVariant="default"
          onClose={() => setOpenDialog(null)}
          onError={setTopError}
          onConfirm={() => api.reactivatePerson(openDialog.person.id)}
          onSuccess={async () => {
            setOpenDialog(null);
            setTopError(null);
            await invalidatePeople();
          }}
        />
      ) : null}
    </AppShell>
  );
}

function EmployeeIdFormatCard({ onError }: { onError: (msg: string | null) => void }): JSX.Element {
  const queryClient = useQueryClient();
  const formatsQuery = useQuery({
    queryKey: ['employee-id-format'],
    queryFn: () => api.listEmployeeIdFormats(),
  });
  const [adding, setAdding] = React.useState(false);
  const [prefix, setPrefix] = React.useState('');
  const [yearDigits, setYearDigits] = React.useState<2 | 4>(2);
  const [seqDigits, setSeqDigits] = React.useState(4);
  const [validFrom, setValidFrom] = React.useState(todayIst());

  const formats: EmployeeIdFormatView[] = formatsQuery.data ?? [];
  const current = formats.find((f) => f.isCurrent) ?? null;

  const add = useMutation({
    mutationFn: () =>
      api.addEmployeeIdFormat({
        prefix: prefix.trim().toUpperCase(),
        yearDigits,
        seqDigits,
        validFrom,
      }),
    onSuccess: async () => {
      setAdding(false);
      setPrefix('');
      onError(null);
      await queryClient.invalidateQueries({ queryKey: ['employee-id-format'] });
    },
    onError: (err) => onError(toMessage(err)),
  });

  const preview = (() => {
    const p = prefix.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9]{0,15}$/.test(p)) return null;
    const year = validFrom.slice(0, 4).slice(-yearDigits);
    return `${p}${year}${'1'.padStart(seqDigits, '0')}`;
  })();

  const canSubmit = preview !== null && /^\d{4}-\d{2}-\d{2}$/.test(validFrom) && !add.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Employee ID format</CardTitle>
        <CardDescription>
          IDs are allocated as prefix + join year + a per-year sequence, and never change once
          issued. A new format applies to people whose join date falls on or after its start.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {formatsQuery.isPending ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
        ) : (
          <div className="flex flex-col gap-2">
            {formats.map((f) => (
              <div
                key={f.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm"
              >
                <code className="font-mono text-base font-semibold">{f.example}</code>
                <span className="text-[hsl(var(--muted-foreground))]">
                  {f.prefix} + {f.yearDigits}-digit year + {f.seqDigits}-digit sequence
                </span>
                <span className="text-[hsl(var(--muted-foreground))]">
                  from {formatDate(f.validFrom)}
                </span>
                {f.id === current?.id ? <Badge variant="default">Current</Badge> : null}
              </div>
            ))}
          </div>
        )}

        {adding ? (
          <div className="flex flex-col gap-3 rounded-lg border border-[hsl(var(--border))] p-3">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="eif-prefix">Prefix</Label>
                <Input
                  id="eif-prefix"
                  value={prefix}
                  placeholder="BITS"
                  onChange={(e) => setPrefix(e.currentTarget.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="eif-year">Year digits</Label>
                <select
                  id="eif-year"
                  className="h-10 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm"
                  value={yearDigits}
                  onChange={(e) => setYearDigits(e.currentTarget.value === '4' ? 4 : 2)}
                >
                  <option value={2}>2 (e.g. 26)</option>
                  <option value={4}>4 (e.g. 2026)</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="eif-seq">Sequence digits</Label>
                <Input
                  id="eif-seq"
                  type="number"
                  min={2}
                  max={6}
                  value={seqDigits}
                  onChange={(e) => setSeqDigits(Number(e.currentTarget.value))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="eif-from">Applies to joins from</Label>
                <Input
                  id="eif-from"
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.currentTarget.value)}
                />
              </div>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              First ID under this format:{' '}
              {preview ? (
                <code className="font-mono font-semibold text-[hsl(var(--foreground))]">
                  {preview}
                </code>
              ) : (
                'enter a prefix that starts with a letter'
              )}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
              <Button disabled={!canSubmit} onClick={() => add.mutate()}>
                {add.isPending ? 'Saving…' : 'Save format'}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button variant="outline" onClick={() => setAdding(true)}>
              Add a new format
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreatePersonDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => Promise<void> | void;
}): JSX.Element {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [dateJoined, setDateJoined] = React.useState(todayIst());
  const [roles, setRoles] = React.useState<Set<RoleName>>(new Set(['Employee']));
  const [initialPassword, setInitialPassword] = React.useState('');
  const [employeeId, setEmployeeId] = React.useState('');
  const [employeeIdEdited, setEmployeeIdEdited] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [created, setCreated] = React.useState<{ name: string; employeeId: string } | null>(null);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setEmail('');
      setPhone('');
      setDateJoined(todayIst());
      setRoles(new Set(['Employee']));
      setInitialPassword('');
      setEmployeeId('');
      setEmployeeIdEdited(false);
      setError(null);
      setCreated(null);
    }
  }, [open]);

  // The next number depends on the join year, so it follows the date until the
  // admin types their own value.
  const nextIdQuery = useQuery({
    queryKey: ['next-employee-id', dateJoined],
    queryFn: () => api.nextEmployeeId(dateJoined),
    enabled: open && /^\d{4}-\d{2}-\d{2}$/.test(dateJoined),
  });
  React.useEffect(() => {
    if (!employeeIdEdited && nextIdQuery.data) setEmployeeId(nextIdQuery.data.employeeId);
  }, [nextIdQuery.data, employeeIdEdited]);
  const suggestedId = nextIdQuery.data?.employeeId ?? '';
  const usingCustomId = employeeId.trim() !== '' && employeeId.trim().toUpperCase() !== suggestedId;

  const create = useMutation({
    mutationFn: () =>
      api.createPerson({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        dateJoined,
        roles: [...roles],
        initialPassword,
        employeeId: employeeId.trim() || undefined,
      }),
    onSuccess: async (res) => {
      setCreated({ name: name.trim(), employeeId: res.employeeId });
      await onSuccess();
    },
    onError: (err) => setError(toMessage(err)),
  });

  if (created) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Person created</DialogTitle>
            <DialogDescription>
              {created.name} signs in with this employee ID and the initial password you set.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Employee ID</Label>
              <div className="flex items-center gap-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3">
                <code
                  className="flex-1 font-mono text-lg font-semibold tracking-wide"
                  data-testid="created-employee-id"
                >
                  {created.employeeId}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(created.employeeId);
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              The ID also appears in the people list. They will be asked to change their password on
              first sign-in.
            </p>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const canSubmit =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    roles.size > 0 &&
    initialPassword.length >= 8 &&
    !create.isPending;

  const toggleRole = (r: RoleName): void => {
    setRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New person</DialogTitle>
          <DialogDescription>
            Admins with the Finance role must complete 2FA setup at first login.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="np-name">Name</Label>
            <Input id="np-name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="np-email">Email</Label>
            <Input
              id="np-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="np-phone">Phone (optional)</Label>
            <Input id="np-phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="np-joined">Date joined</Label>
            <Input
              id="np-joined"
              type="date"
              value={dateJoined}
              onChange={(e) => setDateJoined(e.currentTarget.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="np-empid">Employee ID</Label>
            <div className="flex items-center gap-2">
              <Input
                id="np-empid"
                className="font-mono uppercase"
                value={employeeId}
                placeholder={
                  nextIdQuery.isPending ? 'Generating…' : suggestedId || 'e.g. BITS260005'
                }
                onChange={(e) => {
                  setEmployeeId(e.currentTarget.value.toUpperCase());
                  setEmployeeIdEdited(true);
                }}
              />
              {usingCustomId && suggestedId ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEmployeeId(suggestedId);
                    setEmployeeIdEdited(false);
                  }}
                >
                  Use {suggestedId}
                </Button>
              ) : null}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              {usingCustomId
                ? 'Custom ID — it must be unique. Letters, digits, . _ - only.'
                : 'Auto-generated from the join year. Edit it if this person already has an ID.'}
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_ROLES.map((r) => (
                <label
                  key={r}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm hover:bg-[hsl(var(--accent))]"
                >
                  <input type="checkbox" checked={roles.has(r)} onChange={() => toggleRole(r)} />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="np-pw">Initial password (min 8 chars)</Label>
            <Input
              id="np-pw"
              type="password"
              value={initialPassword}
              onChange={(e) => setInitialPassword(e.currentTarget.value)}
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create person'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
        {label}
      </span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function PersonDetailsDialog({
  person,
  onClose,
  onError,
  onSuccess,
}: {
  person: PersonDetail;
  onClose: () => void;
  onError: (msg: string | null) => void;
  onSuccess: () => Promise<void> | void;
}): JSX.Element {
  const editable = person.status === 'active';
  const [name, setName] = React.useState(person.name);
  const [email, setEmail] = React.useState(person.email);
  const [phone, setPhone] = React.useState(person.phone ?? '');
  const [dateJoined, setDateJoined] = React.useState(person.dateJoined);
  const [employeeId, setEmployeeId] = React.useState(person.employeeId ?? '');
  const [error, setError] = React.useState<string | null>(null);

  const employeeIdChanged = employeeId.trim().toUpperCase() !== (person.employeeId ?? '');
  const dirty =
    name.trim() !== person.name ||
    email.trim() !== person.email ||
    (phone.trim() || null) !== person.phone ||
    dateJoined !== person.dateJoined ||
    employeeIdChanged;

  const update = useMutation({
    mutationFn: () =>
      api.updatePerson(person.id, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim().length > 0 ? phone.trim() : null,
        dateJoined,
        ...(employeeIdChanged ? { employeeId: employeeId.trim() } : {}),
      }),
    onSuccess: async () => {
      await onSuccess();
    },
    onError: (err) => {
      const msg = toMessage(err);
      setError(msg);
      onError(msg);
    },
  });

  const canSave =
    editable &&
    dirty &&
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    employeeId.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateJoined) &&
    !update.isPending;

  return (
    <Dialog open onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{person.name}</DialogTitle>
          <DialogDescription>
            {editable
              ? 'Roles, password, 2FA and exit have their own actions. Changing the employee ID changes what this person signs in with.'
              : 'This person has exited. Reactivate them before editing.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-4 md:grid-cols-3">
            <DetailRow label="Employee ID">
              {person.employeeId ? (
                <span className="flex items-center gap-2">
                  <code className="font-mono font-semibold">{person.employeeId}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(person.employeeId ?? '');
                    }}
                  >
                    Copy
                  </Button>
                </span>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="Status">
              <Badge variant={person.status === 'active' ? 'default' : 'muted'}>
                {person.status}
              </Badge>
              {person.dateLeft ? (
                <span className="ml-2 text-[hsl(var(--muted-foreground))]">
                  left {formatDate(person.dateLeft)}
                </span>
              ) : null}
            </DetailRow>
            <DetailRow label="Roles">
              <span className="flex flex-wrap gap-1">
                {person.roles.map((r) => (
                  <Badge key={r} variant="muted">
                    {r}
                  </Badge>
                ))}
              </span>
            </DetailRow>
            <DetailRow label="2FA">
              {person.twoFactorVerified ? 'Verified' : 'Not enrolled'}
            </DetailRow>
            <DetailRow label="Password">
              {person.mustChangePassword ? 'Must change at next sign-in' : 'Set'}
            </DetailRow>
            <DetailRow label="C-suite">
              {person.isCsuite
                ? person.csuiteRole
                  ? person.csuiteRole.toUpperCase()
                  : 'Yes'
                : 'No'}
            </DetailRow>
            <DetailRow label="Created">
              {formatDateTime(person.createdAt)}
              <span className="block text-xs text-[hsl(var(--muted-foreground))]">
                {person.source}
                {person.createdBy ? ` · by ${person.createdBy}` : ''}
              </span>
            </DetailRow>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-name">Name</Label>
              <Input
                id="ep-name"
                value={name}
                disabled={!editable}
                onChange={(e) => setName(e.currentTarget.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-email">Email</Label>
              <Input
                id="ep-email"
                type="email"
                value={email}
                disabled={!editable}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-phone">Phone</Label>
              <Input
                id="ep-phone"
                value={phone}
                disabled={!editable}
                onChange={(e) => setPhone(e.currentTarget.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-joined">Date joined</Label>
              <Input
                id="ep-joined"
                type="date"
                value={dateJoined}
                disabled={!editable}
                onChange={(e) => setDateJoined(e.currentTarget.value)}
              />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                Changing this does not change the employee ID.
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ep-empid">Employee ID</Label>
              <Input
                id="ep-empid"
                className="font-mono uppercase"
                value={employeeId}
                disabled={!editable}
                onChange={(e) => setEmployeeId(e.currentTarget.value.toUpperCase())}
              />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                {employeeIdChanged
                  ? `Was ${person.employeeId ?? '—'}. Must be unique; they will sign in with the new ID from now on.`
                  : 'Used to sign in. Letters, digits, . _ - only.'}
              </span>
            </div>
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button disabled={!canSave} onClick={() => update.mutate()}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SetRolesDialog({
  person,
  onClose,
  onError,
  onSuccess,
}: {
  person: PersonDetail;
  onClose: () => void;
  onError: (msg: string | null) => void;
  onSuccess: () => Promise<void> | void;
}): JSX.Element {
  const [roles, setRoles] = React.useState<Set<RoleName>>(new Set(person.roles));
  const [error, setError] = React.useState<string | null>(null);

  const toggle = (r: RoleName): void => {
    setRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  };

  const save = useMutation({
    mutationFn: () => api.setPersonRoles(person.id, { roles: [...roles] }),
    onSuccess: async () => {
      await onSuccess();
    },
    onError: (err) => {
      const msg = toMessage(err);
      setError(msg);
      onError(msg);
    },
  });

  return (
    <Dialog open onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set roles for {person.name}</DialogTitle>
          <DialogDescription>
            Admin and Finance require 2FA. You cannot remove the final Admin.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            {ALL_ROLES.map((r) => (
              <label
                key={r}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm hover:bg-[hsl(var(--accent))]"
              >
                <input type="checkbox" checked={roles.has(r)} onChange={() => toggle(r)} />
                <span>{r}</span>
              </label>
            ))}
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={save.isPending || roles.size === 0} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save roles'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  person,
  onClose,
  onError,
  onSuccess,
}: {
  person: PersonDetail;
  onClose: () => void;
  onError: (msg: string | null) => void;
  onSuccess: () => Promise<void> | void;
}): JSX.Element {
  const [newPassword, setNewPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => api.resetPersonPassword(person.id, { newPassword }),
    onSuccess: async () => {
      await onSuccess();
    },
    onError: (err) => {
      const msg = toMessage(err);
      setError(msg);
      onError(msg);
    },
  });

  return (
    <Dialog open onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password for {person.name}</DialogTitle>
          <DialogDescription>
            Existing sessions stay valid. Share the new password out-of-band.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rp-pw">New password (min 8 chars)</Label>
            <Input
              id="rp-pw"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.currentTarget.value)}
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={reset.isPending || newPassword.length < 8}
              onClick={() => reset.mutate()}
            >
              {reset.isPending ? 'Resetting…' : 'Reset password'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExitPersonDialog({
  person,
  onClose,
  onError,
  onSuccess,
}: {
  person: PersonDetail;
  onClose: () => void;
  onError: (msg: string | null) => void;
  onSuccess: () => Promise<void> | void;
}): JSX.Element {
  const [dateLeft, setDateLeft] = React.useState(todayIst());
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const exit = useMutation({
    mutationFn: () => api.exitPerson(person.id, { dateLeft, reason: reason.trim() }),
    onSuccess: async () => {
      await onSuccess();
    },
    onError: (err) => {
      const msg = toMessage(err);
      setError(msg);
      onError(msg);
    },
  });

  return (
    <Dialog open onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Exit {person.name}</DialogTitle>
          <DialogDescription>
            Sessions and API tokens are revoked immediately. History is preserved.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ex-date">Date left</Label>
            <Input
              id="ex-date"
              type="date"
              value={dateLeft}
              onChange={(e) => setDateLeft(e.currentTarget.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ex-reason">Reason</Label>
            <Input
              id="ex-reason"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              maxLength={500}
              placeholder="Resignation / termination / contract ended"
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={exit.isPending || reason.trim().length === 0}
              onClick={() => exit.mutate()}
            >
              {exit.isPending ? 'Exiting…' : 'Exit person'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({
  title,
  description,
  confirmLabel,
  confirmVariant,
  onClose,
  onError,
  onConfirm,
  onSuccess,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant: 'default' | 'destructive';
  onClose: () => void;
  onError: (msg: string | null) => void;
  onConfirm: () => Promise<unknown>;
  onSuccess: () => Promise<void> | void;
}): JSX.Element {
  const [error, setError] = React.useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => onConfirm(),
    onSuccess: async () => {
      await onSuccess();
    },
    onError: (err) => {
      const msg = toMessage(err);
      setError(msg);
      onError(msg);
    },
  });
  return (
    <Dialog open onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant={confirmVariant} disabled={mut.isPending} onClick={() => mut.mutate()}>
              {mut.isPending ? 'Working…' : confirmLabel}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type StatusFilter = 'active' | 'exited' | 'all';

const STATUS_FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'exited', label: 'Exited' },
  { value: 'all', label: 'All' },
];

const ROLE_RANK: Record<RoleName, number> = {
  Admin: 0,
  Finance: 1,
  HR: 2,
  PM: 3,
  Sales: 4,
  Employee: 5,
};

function byRoleRank(a: RoleName, b: RoleName): number {
  return ROLE_RANK[a] - ROLE_RANK[b];
}

type PeopleSortKey = 'name' | 'employeeId' | 'roles' | 'security' | 'status' | 'joined';

const PEOPLE_INITIAL_SORT: SortState<PeopleSortKey> = { key: 'name', direction: 'asc' };

function securityRank(p: PersonDetail): number {
  if (p.status === 'exited') return 3;
  if (!p.twoFactorVerified) return 2;
  return p.mustChangePassword ? 1 : 0;
}

const PEOPLE_SORT_ACCESSORS: SortAccessors<PersonDetail, PeopleSortKey> = {
  name: (p) => p.name,
  employeeId: (p) => p.employeeId,
  roles: (p) => [...p.roles].sort(byRoleRank).join(', '),
  security: securityRank,
  status: (p) => p.status,
  joined: (p) => p.dateJoined,
};

function isSelfVisible(list: PersonDetail[], meId: string | null): boolean {
  return meId !== null && list.some((p) => p.id === meId);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

function Avatar({ name, muted }: { name: string; muted: boolean }): JSX.Element {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
        muted
          ? 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
          : 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
      )}
    >
      {initials(name)}
    </span>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  hint: string;
  tone?: 'neutral' | 'good' | 'warn';
}): JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-5">
        <span className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
          {label}
        </span>
        <span
          className={cn(
            'font-display text-3xl font-semibold tabular-nums',
            tone === 'warn' && value > 0 && 'text-amber-700',
            tone === 'good' && 'text-emerald-700',
          )}
        >
          {value}
        </span>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{hint}</span>
      </CardContent>
    </Card>
  );
}

function SecurityCell({ person }: { person: PersonDetail }): JSX.Element {
  if (person.status === 'exited') {
    return <span className="text-xs text-[hsl(var(--muted-foreground))]">Sessions revoked</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {person.twoFactorVerified ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
          <ShieldCheck className="size-3.5" aria-hidden />
          2FA enrolled
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
          <ShieldAlert className="size-3.5" aria-hidden />
          2FA not set up
        </span>
      )}
      {person.mustChangePassword ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
          <KeyRound className="size-3.5" aria-hidden />
          Password reset pending
        </span>
      ) : null}
    </div>
  );
}

function StatusCell({ person }: { person: PersonDetail }): JSX.Element {
  if (person.status === 'active') {
    return (
      <span className="inline-flex items-center gap-2 text-xs font-medium text-[hsl(var(--foreground))]">
        <span aria-hidden className="size-2 rounded-full bg-emerald-500" />
        Active
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-2 text-xs font-medium">
        <span aria-hidden className="size-2 rounded-full bg-[hsl(var(--destructive))]" />
        Exited
      </span>
      {person.dateLeft ? (
        <span className="pl-4 text-xs text-[hsl(var(--muted-foreground))]">
          {formatDate(person.dateLeft)}
        </span>
      ) : null}
    </span>
  );
}

function RowActionsMenu({
  person,
  isSelf,
  onAction,
}: {
  person: PersonDetail;
  isSelf: boolean;
  onAction: (kind: DialogKind) => void;
}): JSX.Element {
  const active = person.status === 'active';
  const itemClass =
    'flex cursor-pointer select-none items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm outline-none data-[highlighted]:bg-[hsl(var(--accent))] data-[highlighted]:text-[hsl(var(--accent-foreground))] data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50';
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="size-9 px-0"
          aria-label={`Actions for ${person.name}`}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-56 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-1.5 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_16px_40px_-12px_rgba(16,24,40,0.22)]"
        >
          <DropdownMenu.Item className={itemClass} onSelect={() => onAction('edit')}>
            <UserCog className="size-4" aria-hidden />
            View details
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-[hsl(var(--border))]" />
          <DropdownMenu.Item
            className={itemClass}
            disabled={!active || isSelf}
            onSelect={() => onAction('roles')}
          >
            <ShieldCheck className="size-4" aria-hidden />
            <span className="flex flex-col">
              Change roles
              {isSelf ? (
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  Not available on your own account
                </span>
              ) : null}
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={itemClass}
            disabled={!active}
            onSelect={() => onAction('password')}
          >
            <KeyRound className="size-4" aria-hidden />
            Reset password
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={itemClass}
            disabled={!active}
            onSelect={() => onAction('reset-2fa')}
          >
            <ShieldAlert className="size-4" aria-hidden />
            Reset 2FA
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-[hsl(var(--border))]" />
          {active ? (
            <DropdownMenu.Item
              className={cn(
                itemClass,
                'text-[hsl(var(--destructive))] data-[highlighted]:bg-[hsl(var(--destructive))]/10 data-[highlighted]:text-[hsl(var(--destructive))]',
              )}
              disabled={isSelf}
              onSelect={() => onAction('exit')}
            >
              <LogOut className="size-4" aria-hidden />
              <span className="flex flex-col">
                Mark as exited
                {isSelf ? (
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    You cannot exit yourself
                  </span>
                ) : null}
              </span>
            </DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item className={itemClass} onSelect={() => onAction('reactivate')}>
              <RotateCcw className="size-4" aria-hidden />
              Reactivate
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function TableSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col divide-y divide-[hsl(var(--border))]" aria-busy>
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-8 py-3">
          <div className="size-9 animate-pulse rounded-full bg-[hsl(var(--muted))]" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-3 w-40 animate-pulse rounded bg-[hsl(var(--muted))]" />
            <div className="h-2.5 w-56 animate-pulse rounded bg-[hsl(var(--muted))]" />
          </div>
          <div className="h-5 w-20 animate-pulse rounded-full bg-[hsl(var(--muted))]" />
          <div className="h-5 w-24 animate-pulse rounded-full bg-[hsl(var(--muted))]" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 px-8 py-14 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
        <Users className="size-5" aria-hidden />
      </span>
      <div className="flex flex-col gap-1">
        <p className="font-medium">{title}</p>
        <p className="max-w-sm text-sm text-[hsl(var(--muted-foreground))]">{body}</p>
      </div>
      {action}
    </div>
  );
}
